// src/components/chat/GlobalPlaybookCitationTooltip.tsx

/**
 * GlobalPlaybookCitationTooltip
 *
 * Single playbook-tooltip instance rendered via portal at root level.
 * Subscribes to PlaybookCitationTooltipContext so it never unmounts during
 * streaming. Sibling of GlobalDocumentCitationTooltip.
 */

import React from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import type { PlaybookCitationTooltipData } from "../../contexts/PlaybookCitationTooltipContext";
import { usePlaybookCitationTooltip } from "../../contexts/PlaybookCitationTooltipContext";
import { PlaybookSource } from "../../interfaces/documentCitationTypes";
import { PlaybookIcon } from "./DocumentCitationIcons";
import { useTooltipPosition } from "./hooks/useTooltipPosition";
import { usePinnedTooltipDismiss } from "./hooks/usePinnedTooltipDismiss";
import { CitationTooltipPager, CitationTooltipPages } from "./CitationGroupControls";

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  // Snap to the last word boundary before maxLength so we never clip mid-word
  // (e.g. "shall no…" instead of "shall not…"). Falls back to a hard cut when
  // there's no space in the prefix (very long single token).
  const cut = text.lastIndexOf(" ", maxLength);
  const end = cut > 0 ? cut : maxLength;
  return text.substring(0, end) + "…";
}

/**
 * Strip leading markdown heading markers ("# ", "## ", ...) from each line.
 * BE preserves headings in chunks for the LLM; we don't want them in the tooltip.
 */
function stripHeadingMarkers(text: string): string {
  return text.replace(/^\s*#{1,6}\s+/gm, "");
}

const STATUS_STYLE: Record<PlaybookSource["status"], { bg: string; fg: string; label: string }> = {
  approved: { bg: "#E6F4EA", fg: "#1E7E34", label: "Approved" },
  pending: { bg: "#FFF4E5", fg: "#B26A00", label: "Pending" },
  unmatched: { bg: "#FDECEA", fg: "#B71C1C", label: "Unmatched" },
};

const StatusPill: React.FC<{ status: PlaybookSource["status"] }> = ({ status }) => {
  const s = STATUS_STYLE[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        backgroundColor: s.bg,
        color: s.fg,
        borderRadius: "10px",
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
};

/**
 * Element overrides for cited_text markdown.
 *
 * The excerpt block is rendered in italic (quote convention), so an inline
 * `<em>` would land as italic-on-italic and visually disappear. We flip it to
 * upright + medium weight so emphasized spans (e.g. `_[importance 3]_`) read
 * as the standout text within the italic block. All other block-level markdown
 * (lists, headings, paragraphs) is flattened to inline so the excerpt flows
 * as one tight quote.
 *
 * Defined at module scope so the reference is stable across renders — keeps
 * ReactMarkdown from re-instantiating element renderers on every hover.
 */
const CITED_TEXT_MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  em: ({ children }: { children?: React.ReactNode }) => (
    <span style={{ fontStyle: "normal", fontWeight: 500, color: "#333" }}>{children}</span>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong style={{ fontWeight: 600, color: "#333" }}>{children}</strong>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  ol: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  li: ({ children }: { children?: React.ReactNode }) => (
    <span style={{ display: "block" }}>• {children}</span>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  h2: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  h3: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  h4: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  h5: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  h6: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#1565C0", textDecoration: "none" }}
    >
      {children}
    </a>
  ),
};

const ImportancePill: React.FC<{ importance: number }> = ({ importance }) => (
  <span
    style={{
      display: "inline-block",
      padding: "1px 6px",
      backgroundColor: "#EEF0FB",
      color: "#3949AB",
      border: "1px solid #C5CAE9",
      borderRadius: "10px",
      fontSize: "9px",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
    title="Importance (1 = lowest, 3 = highest)"
  >
    {`I${importance}`}
  </span>
);

const PlaybookTooltipTitle: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      fontSize: "12px",
      fontWeight: 500,
      color: "#212529",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
    }}
    title={title}
  >
    {title}
  </div>
);

/** Breadcrumb, quote and sources for one citation. */
const PlaybookTooltipBody: React.FC<{ item: PlaybookCitationTooltipData }> = ({ item }) => {
  const { breadcrumb, importance, citedText, sources } = item;
  const cleanedCitedText = stripHeadingMarkers(citedText);

  return (
    <>
      {/* Breadcrumb + importance pill */}
      {breadcrumb.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "11px",
            color: "#3949AB",
            fontWeight: 500,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={breadcrumb.join(" › ")}
          >
            {breadcrumb.join(" › ")}
          </span>
          {importance != null && <ImportancePill importance={importance} />}
        </div>
      )}

      {/* Cited text excerpt */}
      {cleanedCitedText && (
        <div
          style={{
            fontSize: "11px",
            lineHeight: 1.5,
            color: "#555",
            fontStyle: "italic",
            wordBreak: "break-word",
            borderLeft: "2px solid #C5CAE9",
            paddingLeft: "8px",
          }}
        >
          <ReactMarkdown components={CITED_TEXT_MARKDOWN_COMPONENTS}>
            {truncateText(cleanedCitedText, 200)}
          </ReactMarkdown>
        </div>
      )}

      {/* Sources */}
      {sources.length === 0 ? (
        <div style={{ fontSize: "10px", color: "#9E9E9E", fontStyle: "italic" }}>
          No linked sources
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {sources.map((s, i) => (
            <li
              key={`${s.url || s.label}-${i}`}
              style={{ display: "flex", flexDirection: "column", gap: "2px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {s.url ? (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#1565C0",
                      textDecoration: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.label || s.url}
                  </a>
                ) : (
                  <span style={{ fontSize: "11px", fontWeight: 500, color: "#424242" }}>
                    {s.label}
                  </span>
                )}
                <StatusPill status={s.status} />
              </div>
              {s.quote && (
                <span
                  style={{
                    fontSize: "10px",
                    color: "#757575",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {truncateText(s.quote, 140)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export const GlobalPlaybookCitationTooltip: React.FC = () => {
  const { state, handleTooltipMouseEnter, handleTooltipMouseLeave, pageTooltip, closeTooltip } =
    usePlaybookCitationTooltip();
  const {
    isVisible,
    anchorRect,
    documentTitle,
    breadcrumb,
    importance,
    citedText,
    sources,
    group,
    pinned,
  } = state;
  const { ref: tooltipRef, position } = useTooltipPosition<HTMLDivElement>(anchorRect, isVisible);

  usePinnedTooltipDismiss(isVisible && pinned, tooltipRef, group?.anchorEl ?? null, closeTooltip);

  if (!isVisible) return null;

  // A merged chip's citations, paged; null for a single citation
  const pages = group && group.items.length > 1 ? group : null;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="playbook-citation-tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      role="tooltip"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
        maxWidth: "340px",
        minWidth: "240px",
        maxHeight: `${position.maxHeight}px`,
        overflowY: "auto",
        padding: "12px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #C5CAE9",
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        pointerEvents: "auto",
        fontFamily: "'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      {/* Header: playbook icon + title */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <PlaybookIcon size={24} />
        {pages ? (
          <CitationTooltipPages index={pages.index} style={{ flex: 1, minWidth: 0 }}>
            {pages.items.map((item, i) => (
              <PlaybookTooltipTitle key={i} title={item.documentTitle} />
            ))}
          </CitationTooltipPages>
        ) : (
          <PlaybookTooltipTitle title={documentTitle} />
        )}
        {pages && (
          <CitationTooltipPager
            index={pages.index}
            total={pages.items.length}
            onPage={pageTooltip}
          />
        )}
      </div>

      {pages ? (
        <CitationTooltipPages index={pages.index}>
          {pages.items.map((item, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <PlaybookTooltipBody item={item} />
            </div>
          ))}
        </CitationTooltipPages>
      ) : (
        <PlaybookTooltipBody item={{ documentTitle, breadcrumb, importance, citedText, sources }} />
      )}
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default GlobalPlaybookCitationTooltip;
